import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import { CheckCircle2, XCircle } from 'lucide-react'
import { cn } from 'ui/lib/utils'

import { useScoreStore } from '~/stores/score'

type ScoreProps = {
  activeCardNumber: number
  cardsLength: number
  guessedNumber: number
  notGuessedNumber: number
  className?: string
}

export const Score = ({ guessedNumber, notGuessedNumber, className, activeCardNumber, cardsLength }: ScoreProps) => {
  const isGuessedActive = useScoreStore(state => state.isGuessedActive)
  const isNotGuessedActive = useScoreStore(state => state.isNotGuessedActive)
  const isSnoozeActive = useScoreStore(state => state.isSnoozeActive)
  const { t } = useTranslation()

  const unknownDisplay = notGuessedNumber + (isNotGuessedActive ? 1 : 0)
  const knownDisplay = guessedNumber + (isGuessedActive ? 1 : 0)
  const unknownVisible = unknownDisplay || isNotGuessedActive
  const knownVisible = knownDisplay || isGuessedActive

  const answeredTotal = knownDisplay + unknownDisplay
  const knownAnswerPct = answeredTotal ? (knownDisplay / answeredTotal) * 100 : 0

  return (
    <section
      aria-label={t('Study session', { current: activeCardNumber, total: cardsLength })}
      className={cn('w-full px-1', className)}
      role="region"
    >
      <div className="overflow-hidden rounded-2xl bg-white/20 shadow-lg shadow-black/[0.06] ring-1 ring-inset ring-black/[0.04] backdrop-blur-2xl backdrop-saturate-150 dark:border-white/[0.12] dark:bg-slate-950/40 dark:shadow-black/40 dark:ring-white/[0.06]">
        <div className="flex items-stretch justify-between gap-2 px-2 py-2 sm-landscape:py-1 sm:gap-3 sm:px-3 sm:py-2.5">
          <div className="flex min-w-0 flex-1 flex-col items-start justify-center">
            <div
              className={clsx(
                'flex items-center gap-1 transition-[transform,opacity] duration-200 sm:gap-1.5',
                isNotGuessedActive && 'scale-[1.04]',
                !unknownVisible && 'invisible',
              )}
            >
              <XCircle
                className={clsx(
                  'size-[1.05rem] shrink-0 text-rose-500/75 sm:size-[1.15rem]',
                  isNotGuessedActive && 'text-rose-500 dark:text-rose-400',
                )}
                strokeWidth={1.75}
                aria-hidden
              />
              <span
                aria-label={t('Number of unknown cards')}
                className="text-lg font-semibold tabular-nums tracking-tight text-slate-700 dark:text-slate-200 sm:text-xl"
              >
                {unknownDisplay}
              </span>
            </div>
          </div>

          <div className="flex min-w-0 items-center justify-center px-1">
            <div className="flex items-center gap-2">
              <span
                aria-label={t('Current card number')}
                className="text-xl font-semibold tabular-nums leading-none text-slate-800 dark:text-slate-100 sm:text-2xl"
              >
                {activeCardNumber}
              </span>
              <div className="h-5 w-px shrink-0 rounded-full bg-slate-300/90 dark:bg-slate-600/80" aria-hidden />
              <span
                aria-label={t('Total cards')}
                className="text-sm font-medium tabular-nums text-slate-500 dark:text-slate-400"
              >
                {cardsLength}
              </span>
            </div>
          </div>

          <div className="flex min-w-0 flex-1 flex-col items-end justify-center">
            <div
              className={clsx(
                'flex items-center gap-1 transition-[transform,opacity] duration-200 sm:gap-1.5',
                isGuessedActive && 'scale-[1.04]',
                !knownVisible && 'invisible',
              )}
            >
              <span
                aria-label={t('Number of known cards')}
                className="text-lg font-semibold tabular-nums tracking-tight text-slate-700 dark:text-slate-200 sm:text-xl"
              >
                {knownDisplay}
              </span>
              <CheckCircle2
                className={clsx(
                  'size-[1.05rem] shrink-0 sm:size-[1.15rem]',
                  isSnoozeActive ? 'text-orange-500/80 dark:text-orange-300' : 'text-emerald-500/75',
                  isGuessedActive && !isSnoozeActive && 'text-emerald-600 dark:text-emerald-400',
                )}
                strokeWidth={1.75}
                aria-hidden
              />
            </div>
          </div>
        </div>

        <div
          className="flex h-0.5 w-full overflow-hidden rounded-b-2xl bg-slate-200/55 dark:bg-slate-800/75"
          aria-hidden
        >
          {!answeredTotal ? (
            <div className="h-full w-full bg-slate-300/35 dark:bg-slate-700/45" />
          ) : (
            <>
              <div
                className="dark:bg-rose-400/28 h-full bg-rose-500/35 transition-[width] duration-300 ease-out"
                style={{ width: `${100 - knownAnswerPct}%` }}
              />
              <div
                className={clsx(
                  'h-full transition-[width] duration-300 ease-out',
                  isSnoozeActive
                    ? 'bg-orange-500/40 dark:bg-orange-300/35'
                    : 'dark:bg-emerald-400/28 bg-emerald-500/35',
                )}
                style={{ width: `${knownAnswerPct}%` }}
              />
            </>
          )}
        </div>
      </div>
    </section>
  )
}
